import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {href, type LinkDescriptor, useNavigate} from 'react-router';

import MailIcon from '@/assets/icons/mail.svg?react';
import PageLanguageSwitcher from '@/features/pageLanguageSwitcher';
import {isApiError} from '@/shared/api';
import {
  ErrorHandler,
  SignRateLimitCase,
  signInViaApple,
  signInViaGoogle,
  UserNotRegisteredCase,
} from '@/shared/api/auth';
import AppleOauthButton, {
  AppleAuthResponse,
  useIsSupportedAppleOauthButton,
} from '@/shared/components/AppleOauthButton';
import GoogleOauthButton, {
  CredentialResponse,
} from '@/shared/components/GoogleOauthButton';
import DialogModal from '@/shared/components/modals/base/DialogModal';
import SizeObserver from '@/shared/components/SizeObserver';
import {useInAppBrowser} from '@/shared/hooks/useInAppBrowser';
import {getMediaUrl} from '@/shared/lib/getMediaUrl';
import {setAppleCode, setGoogleToken} from '@/shared/lib/localStorage';
import {
  AUTH_EVENTS,
  analyticsEvent,
  analyticsScreen,
  analyticsSignIn,
  defaultRegistrationTrackScreen,
  SCREENS,
  trackDefaultRegistrationEvent,
  trackInternal,
} from '@/shared/services/analytics';
import {getLanguage, localize} from '@/shared/services/i18n';
import {afterSignIn} from '@/sync/actions/afterSignIn';
import {useAutoRedirectToBetterOrManualLanguage} from '@/sync/hooks/useAutoRedirectToBetterOrManualLanguage';
import {localizeHref} from '@/sync/lib/localizeHref';
import {handleLoginSuccess} from '@/utils_legacy/auth';
import Footer from '@/widgets/footer';

import AuthSection from '../../shared/components/auth/AuthSection';
import AppsSection from './components/AppsSection';
import AdvantageSection from './components/advantageSection/AdvantageSection';
import MembersSection from './components/membersSection/MembersSection';
import RatedSection from './components/RatedSetcion';
import StepsSection from './components/stepsSection/StepsSection';

const Login: FC = () => {
  const navigate = useNavigate();
  const inAppBrowser = useInAppBrowser();
  const isSupportedAppleOauthButton = useIsSupportedAppleOauthButton();
  const supportThirdPartyLogins = !inAppBrowser;

  useAutoRedirectToBetterOrManualLanguage();

  const [dialogError, setDialogError] = useState<{
    title?: string;
    message: string;
  } | null>(null);

  const handleEmailLogin = () => {
    analyticsEvent(AUTH_EVENTS.SIGN_IN_WITH_EMAIL_PRESSED);
    trackDefaultRegistrationEvent(AUTH_EVENTS.SIGN_IN_WITH_EMAIL_PRESSED);
    trackInternal('email_sign_up_button_clicked');
    navigate(localizeHref('/:lang?/email-signin/:step?'));
  };

  useEffect(() => {
    analyticsScreen(SCREENS.SIGN);
    defaultRegistrationTrackScreen(SCREENS.SIGN);
    trackInternal('login_screen_loaded');
  }, []);

  useEffect(() => {
    const authToken = localStorage?.getItem('authToken');
    if (authToken) {
      navigate(href('/app'));
    }
  }, [navigate]);

  const handleClick = () => {
    navigate(localizeHref('/:lang?/email-signin/:step?'));
  };

  const commonErrorsHandler = useMemo(() => {
    return [
      new SignRateLimitCase(() => {
        setDialogError({
          title: localize('sign_rate_limit_alert_title'),
          message: localize('sign_rate_limit_alert_message'),
        });
      }),
    ];
  }, []);

  const handleGoogleLogin = async ({credential}: CredentialResponse) => {
    if (!credential) {
      return;
    }

    try {
      analyticsEvent(AUTH_EVENTS.SIGN_IN_WITH_GOOGLE_SUCCESS);
      trackDefaultRegistrationEvent(AUTH_EVENTS.SIGN_IN_WITH_GOOGLE_SUCCESS);

      const userResponse = await signInViaGoogle(credential);

      if (!handleLoginSuccess(userResponse.result)) {
        return;
      }

      trackInternal('sign_in');
      analyticsSignIn('google');
      trackDefaultRegistrationEvent(AUTH_EVENTS.SIGN_IN, {
        method: 'google',
      });

      await afterSignIn();
      navigate(href('/app'));
    } catch (error) {
      if (!isApiError(error)) {
        return;
      }

      const errorHandler = new ErrorHandler();
      errorHandler.use([
        ...commonErrorsHandler,
        new UserNotRegisteredCase(() => {
          setGoogleToken(credential);
          trackInternal('google_continue_sign_up_passed');
          navigate(localizeHref('/:lang?/user-info'));
        }),
        ...commonErrorsHandler,
      ]);
      errorHandler.handle(error.response!.data);
    }
  };
  const handleAppleLogin = useCallback<(response: AppleAuthResponse) => void>(
    async ({authorization}) => {
      const {code} = authorization;
      if (!code) {
        return;
      }

      try {
        analyticsEvent(AUTH_EVENTS.SIGN_IN_WITH_APPLE_SUCCESS);
        trackDefaultRegistrationEvent(AUTH_EVENTS.SIGN_IN_WITH_APPLE_SUCCESS);

        const userResponse = await signInViaApple(code);

        if (!handleLoginSuccess(userResponse)) {
          return;
        }

        trackInternal('sign_in');
        analyticsSignIn('apple');
        trackDefaultRegistrationEvent(AUTH_EVENTS.SIGN_IN, {
          method: 'apple',
        });

        await afterSignIn();
        navigate(href('/app'));
      } catch (error) {
        if (!isApiError(error)) {
          return;
        }

        const errorHandler = new ErrorHandler();
        errorHandler.use([
          ...commonErrorsHandler,
          new UserNotRegisteredCase(() => {
            setAppleCode(code);
            trackInternal('apple_continue_sign_up_passed');
            navigate(localizeHref('/:lang?/user-info'));
          }),
        ]);
        errorHandler.handle(error.response!.data);
      }
    },
    [navigate, commonErrorsHandler]
  );

  return (
    <>
      <AuthSection langSwitcher={<PageLanguageSwitcher />}>
        {supportThirdPartyLogins && (
          <>
            <SizeObserver className="flex h-[50px] w-[80vw] max-w-[400px] items-center overflow-hidden rounded-lg bg-white p-2 md:w-[96vw]">
              {({width}) => (
                <GoogleOauthButton
                  locale={getLanguage()}
                  onSuccess={handleGoogleLogin}
                  onError={() => console.log('Google Login Failed')}
                  ux_mode="popup"
                  width={Math.ceil(width)}
                  text="continue_with"
                  logo_alignment="center"
                />
              )}
            </SizeObserver>
            {isSupportedAppleOauthButton && (
              <AppleOauthButton
                className="h-[50px] w-[80vw] max-w-[400px]"
                onSuccess={handleAppleLogin}
                onError={() => {
                  console.log('Apple Login Failed');
                }}
                buttonExtraChildren={localize('continue_sign_with_apple')}
              />
            )}
          </>
        )}
        <button
          className="flex h-[50px] w-[80vw] max-w-[400px] items-center justify-center rounded-lg border border-gray-300 bg-transparent px-4 py-3 font-bold text-secondary shadow-xs md:w-[96vw]"
          onClick={handleEmailLogin}
        >
          <span className="mr-4">
            <MailIcon width={20} height={20} fill="white" />
          </span>{' '}
          {localize('continue_sign_with_email')}
        </button>
      </AuthSection>
      <RatedSection />
      <AdvantageSection handleSignInWithEmail={handleClick} />
      <MembersSection handleSignInWithEmail={handleClick} />
      <StepsSection handleSignInWithEmail={handleClick} />
      <AppsSection />
      <Footer />
      {dialogError && (
        <DialogModal
          title={dialogError.title}
          okButton={{
            label: localize('ok'),
            onClick: () => setDialogError(null),
          }}
        >
          <p>{dialogError.message}</p>
        </DialogModal>
      )}
    </>
  );
};

export function links(): LinkDescriptor[] {
  return [
    // force preloads first block cover image for mobile
    {
      rel: 'preload',
      as: 'image',
      href: getMediaUrl('landing/top_mob.webp'),
      media: '(max-width: 640px)',
    },
  ];
}

export default Login;
